import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Editor from '@monaco-editor/react';
import {
  fetchProjectDetail,
  fetchProjectSettings,
  updateProjectSettings,
  SettingsConflictError,
} from '../../lib/api/projects';
import { DangerZone } from './DangerZone';
import { Button, Card, CardBody, CardHeader, useUrlTab } from '../ui';
import { SETTINGS_COPY, REVIEW_AUTO_RETRY_LABELS, fieldError } from './settingsCopy';
import { stackOptionsFor } from '../../../../shared/lib/stackOptions';
import type {
  ProjectSettings,
  ProjectSettingsView,
} from '../../../../shared/types/projects';
import { validateAll } from '../../../../shared/lib/projectValidation';

/**
 * UX-06 — human-readable settings form, with the raw JSON editor moved
 * behind an "Advanced: edit as JSON" toggle instead of living permanently
 * side by side with the form.
 *
 * Internal state:
 *   - `view`         server's last known view (settings + version)
 *   - `draft`        the operator's in-progress edit (object form)
 *   - `jsonText`     the Monaco buffer (string form). Kept in sync with
 *                     draft: `mode` doubles as the "which side is the
 *                     source of truth" flag, since only one of form/json
 *                     is ever interactive at a time.
 *   - `errors`       client-side validation against shared/lib.
 *
 * Save flow:
 *   PUT with `expectedVersion = view.version`. On 409 SettingsConflictError
 *   we show the server's `current` next to my version and the original
 *   so the operator can manually merge before retrying.
 */
export const SettingsTab: React.FC<{ projectId: string }> = ({ projectId }) => {
  const query = useQuery({
    queryKey: ['project', projectId, 'settings'],
    queryFn: () => fetchProjectSettings(projectId),
    enabled: projectId.length > 0,
  });
  // Pull the META so the danger zone shows the project's friendly name
  // in the confirmation modal. Cheap: same query the page header uses.
  const projectQ = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => fetchProjectDetail(projectId),
    enabled: projectId.length > 0,
  });

  if (query.isLoading) {
    return <div className="text-sm text-slate-500">Loading settings…</div>;
  }
  if (query.isError) {
    return (
      <div role="alert" className="text-sm text-danger-700">
        {(query.error as Error).message === 'NOT_FOUND'
          ? `Project "${projectId}" not found.`
          : (query.error as Error).message}
      </div>
    );
  }
  if (!query.data) return null;
  return (
    <div className="space-y-6 pb-16 md:pb-0">
      <SettingsEditor
        projectId={projectId}
        initial={query.data}
        onSaved={(newView) => query.refetch().then(() => newView)}
      />
      <DangerZone
        projectId={projectId}
        projectName={projectQ.data?.name ?? projectId}
      />
    </div>
  );
};

interface EditorProps {
  projectId: string;
  initial: ProjectSettingsView;
  onSaved: (newView: ProjectSettingsView) => Promise<unknown>;
}

const SettingsEditor: React.FC<EditorProps> = ({ projectId, initial, onSaved }) => {
  const [view, setView] = useState<ProjectSettingsView>(initial);
  const [draft, setDraft] = useState<ProjectSettings>(initial.settings);
  const [jsonText, setJsonText] = useState<string>(() =>
    JSON.stringify(initial.settings, null, 2),
  );
  // FR-2: `mode` lives in the URL (`?mode=json`) so support can link
  // straight to the JSON view. It also doubles as the "which editor is
  // the source of truth" flag the sync effect below keys off of.
  const [mode, setModeRaw] = useUrlTab('mode', ['form', 'json'], 'form');
  const [jsonParseError, setJsonParseError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ current: ProjectSettingsView } | null>(null);

  // Sync the dormant editor whenever the active one's value changes.
  useEffect(() => {
    if (mode === 'form') {
      setJsonText(JSON.stringify(draft, null, 2));
      setJsonParseError(null);
    }
  }, [draft, mode]);

  const validation = useMemo(() => validateAll(draft), [draft]);
  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(view.settings),
    [draft, view.settings],
  );

  const handleJsonChange = (next: string | undefined) => {
    const text = next ?? '';
    setJsonText(text);
    try {
      const parsed = JSON.parse(text) as unknown;
      setJsonParseError(null);
      setDraft((parsed ?? {}) as ProjectSettings);
    } catch (err) {
      setJsonParseError((err as Error).message);
    }
  };

  // Leaving JSON mode with invalid JSON blocks the switch and keeps the
  // inline parse error visible instead of silently discarding the edit.
  const setMode = (next: 'form' | 'json') => {
    if (mode === 'json' && next === 'form' && jsonParseError) return;
    setModeRaw(next);
  };

  const updateDraft = (next: ProjectSettings) => {
    setDraft(next);
  };

  const handleSave = async () => {
    setSaveError(null);
    setConflict(null);
    if (!validation.ok) {
      setSaveError(validation.errors[0]);
      return;
    }
    if (jsonParseError) {
      setSaveError('Fix JSON parse errors before saving');
      return;
    }
    setSaving(true);
    try {
      const newView = await updateProjectSettings(projectId, {
        settings: draft,
        expectedVersion: view.version === 0 ? null : view.version,
      });
      setView(newView);
      setDraft(newView.settings);
      setJsonText(JSON.stringify(newView.settings, null, 2));
      await onSaved(newView);
    } catch (err) {
      if (err instanceof SettingsConflictError) {
        setConflict({ current: err.current });
      } else {
        setSaveError((err as Error).message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setDraft(view.settings);
    setJsonText(JSON.stringify(view.settings, null, 2));
    setJsonParseError(null);
    setSaveError(null);
  };

  const acceptServerVersion = () => {
    if (!conflict) return;
    setView(conflict.current);
    setDraft(conflict.current.settings);
    setJsonText(JSON.stringify(conflict.current.settings, null, 2));
    setConflict(null);
  };

  const saveDisabled = !isDirty || saving || !!jsonParseError || !validation.ok;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-slate-600">
          Version <span className="font-mono">{view.version}</span>
          {view.updatedAt && (
            <>
              {' · last edited '}
              {new Date(view.updatedAt).toLocaleString()}
              {view.updatedBy && <> by <span className="font-mono">{view.updatedBy}</span></>}
            </>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="settings-mode-toggle"
            onClick={() => setMode(mode === 'json' ? 'form' : 'json')}
            className="text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline"
          >
            {mode === 'json' ? '← Back to form' : 'Advanced: edit as JSON'}
          </button>
          <div className="hidden md:flex gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleDiscard}
              disabled={!isDirty || saving}
            >
              Discard changes
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={saveDisabled}
              data-testid="save-settings"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </div>
      </div>

      {!validation.ok && (
        <div data-testid="validation-errors" role="alert" className="border border-danger-200 bg-danger-50 text-danger-800 text-xs rounded p-2 space-y-1">
          {validation.errors.map((e, i) => (
            <div key={i}>· {e}</div>
          ))}
        </div>
      )}
      {jsonParseError && (
        <div role="alert" className="border border-danger-200 bg-danger-50 text-danger-800 text-xs rounded p-2">
          JSON parse error: {jsonParseError}
        </div>
      )}
      {saveError && (
        <div role="alert" className="border border-danger-200 bg-danger-50 text-danger-800 text-xs rounded p-2">
          {saveError}
        </div>
      )}

      {conflict && (
        <ConflictBanner
          current={conflict.current}
          mine={draft}
          original={view.settings}
          onAcceptServer={acceptServerVersion}
          onDismiss={() => setConflict(null)}
        />
      )}

      {mode === 'form' ? (
        <FormView draft={draft} onChange={updateDraft} errors={validation.errors} />
      ) : (
        <section aria-label="Raw JSON view" data-testid="settings-json-view">
          <div className="border border-slate-200 rounded-lg overflow-hidden" style={{ height: 480 }}>
            <Editor
              defaultLanguage="json"
              value={jsonText}
              onChange={handleJsonChange}
              options={{
                minimap: { enabled: false },
                fontSize: 12,
                tabSize: 2,
                automaticLayout: true,
                scrollBeyondLastLine: false,
              }}
            />
          </div>
        </section>
      )}

      {/* Sticky footer — Discard/Save duplicated here for screens narrower
          than `md`, where the top action row is hidden. */}
      <div className="md:hidden fixed inset-x-0 bottom-0 z-20 bg-white border-t border-slate-200 p-3 flex gap-2 shadow-card-hover">
        <Button
          type="button"
          variant="ghost"
          size="md"
          className="flex-1"
          onClick={handleDiscard}
          disabled={!isDirty || saving}
        >
          Discard
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          className="flex-1"
          onClick={handleSave}
          disabled={saveDisabled}
          data-testid="save-settings-mobile"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Form view — grouped cards of typed controls, plain-language labels and
// help text (settingsCopy.ts). Multi-model lives in its own tab; this form
// covers stack / retries / review / limits / auto_approve.
// ---------------------------------------------------------------------------

const FormView: React.FC<{
  draft: ProjectSettings;
  onChange: (next: ProjectSettings) => void;
  errors: string[];
}> = ({ draft, onChange, errors }) => {
  const setStackKey = (key: string, value: string) => {
    const stack = { ...(draft.stack ?? {}), [key]: value || undefined };
    onChange({ ...draft, stack });
  };
  const setRetriesKey = (key: 'max_fix_attempts' | 'max_partial_retries', value: number) => {
    onChange({ ...draft, retries: { ...(draft.retries ?? {}), [key]: value } });
  };
  const setReviewKey = <K extends keyof NonNullable<ProjectSettings['review']>>(
    key: K,
    value: NonNullable<ProjectSettings['review']>[K],
  ) => {
    onChange({ ...draft, review: { ...(draft.review ?? {}), [key]: value } });
  };
  const setLimitsKey = (key: keyof NonNullable<ProjectSettings['limits']>, value: number) => {
    onChange({ ...draft, limits: { ...(draft.limits ?? {}), [key]: value } });
  };
  const setAutoApprove = (value: boolean) => onChange({ ...draft, auto_approve: value });

  return (
    <div className="space-y-4" data-testid="settings-form-view">
      <SettingsCard title="Tech stack">
        <Field copyKey="stack.backend" errors={errors}>
          {(id) => (
            <Select
              id={id}
              value={draft.stack?.backend ?? ''}
              onChange={(v) => setStackKey('backend', v)}
              options={[{ value: '', label: '— unset —' }, ...stackOptionsFor('backend')]}
            />
          )}
        </Field>
        <Field copyKey="stack.web" errors={errors}>
          {(id) => (
            <Select
              id={id}
              value={draft.stack?.web ?? ''}
              onChange={(v) => setStackKey('web', v)}
              options={[{ value: '', label: '— unset —' }, ...stackOptionsFor('web')]}
            />
          )}
        </Field>
        <Field copyKey="stack.mobile" errors={errors}>
          {(id) => (
            <Select
              id={id}
              value={draft.stack?.mobile ?? ''}
              onChange={(v) => setStackKey('mobile', v)}
              options={[{ value: '', label: '— unset —' }, ...stackOptionsFor('mobile')]}
            />
          )}
        </Field>
      </SettingsCard>

      <SettingsCard title="Retries">
        <Field copyKey="retries.max_fix_attempts" errors={errors}>
          {(id) => (
            <NumberInput
              id={id}
              value={draft.retries?.max_fix_attempts ?? 3}
              min={0}
              max={10}
              onChange={(n) => setRetriesKey('max_fix_attempts', n)}
            />
          )}
        </Field>
        <Field copyKey="retries.max_partial_retries" errors={errors}>
          {(id) => (
            <NumberInput
              id={id}
              value={draft.retries?.max_partial_retries ?? 1}
              min={0}
              max={5}
              onChange={(n) => setRetriesKey('max_partial_retries', n)}
            />
          )}
        </Field>
      </SettingsCard>

      <SettingsCard title="Review">
        <Field copyKey="review.enabled" errors={errors}>
          {(id) => (
            <Toggle
              id={id}
              value={draft.review?.enabled ?? true}
              onChange={(v) => setReviewKey('enabled', v)}
            />
          )}
        </Field>
        <Field copyKey="review.auto_retry_on" errors={errors}>
          {(id) => (
            <Select
              id={id}
              value={draft.review?.auto_retry_on ?? 'halt'}
              onChange={(v) => setReviewKey('auto_retry_on', v as 'none' | 'halt' | 'halt-and-advisory')}
              options={(['none', 'halt', 'halt-and-advisory'] as const).map((v) => ({
                value: v,
                label: REVIEW_AUTO_RETRY_LABELS[v],
              }))}
            />
          )}
        </Field>
      </SettingsCard>

      <SettingsCard title="Quality & throughput">
        <Field copyKey="limits.qa_test_floor" errors={errors}>
          {(id) => (
            <NumberInput
              id={id}
              value={draft.limits?.qa_test_floor ?? 42}
              min={0}
              max={500}
              onChange={(n) => setLimitsKey('qa_test_floor', n)}
            />
          )}
        </Field>
        <Field copyKey="limits.qa_test_ceiling" errors={errors}>
          {(id) => (
            <NumberInput
              id={id}
              value={draft.limits?.qa_test_ceiling ?? 75}
              min={0}
              max={500}
              onChange={(n) => setLimitsKey('qa_test_ceiling', n)}
            />
          )}
        </Field>
        <Field copyKey="limits.max_concurrent_stories" errors={errors}>
          {(id) => (
            <NumberInput
              id={id}
              value={draft.limits?.max_concurrent_stories ?? 5}
              min={1}
              max={20}
              onChange={(n) => setLimitsKey('max_concurrent_stories', n)}
            />
          )}
        </Field>
        <Field copyKey="limits.max_iterations" errors={errors}>
          {(id) => (
            <NumberInput
              id={id}
              value={draft.limits?.max_iterations ?? 50}
              min={1}
              max={200}
              onChange={(n) => setLimitsKey('max_iterations', n)}
            />
          )}
        </Field>
      </SettingsCard>

      <SettingsCard title="Approvals" columns={1}>
        <Field copyKey="auto_approve" errors={errors}>
          {(id) => <Toggle id={id} value={draft.auto_approve ?? false} onChange={setAutoApprove} />}
        </Field>
      </SettingsCard>

      <Card flat className="px-4 py-3 text-sm text-slate-600">
        <Link
          to="?tab=agents&section=keys"
          className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
        >
          Manage API keys in Agents &amp; Models → API keys →
        </Link>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Field / SettingsCard primitives (UX-06 requirement 3)
// ---------------------------------------------------------------------------

let fieldIdSeq = 0;

const SettingsCard: React.FC<{ title: string; columns?: 1 | 2 | 3; children: React.ReactNode }> = ({
  title,
  columns = 3,
  children,
}) => (
  <Card>
    <CardHeader>
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
    </CardHeader>
    <CardBody>
      <div
        className={
          'grid grid-cols-1 gap-4 ' +
          (columns === 1 ? '' : columns === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3')
        }
      >
        {children}
      </div>
    </CardBody>
  </Card>
);

/**
 * Label + control + help text, with `htmlFor`/`id` wired between the
 * label and whatever control `children` renders. `copyKey` looks up the
 * label/help from settingsCopy.ts (and, via `errors`, any validation
 * message that names this field) so no raw snake_case key ever has to be
 * hand-typed as a visible label.
 */
const Field: React.FC<{
  copyKey: string;
  errors: string[];
  children: (id: string) => React.ReactNode;
}> = ({ copyKey, errors, children }) => {
  const copy = SETTINGS_COPY[copyKey];
  const id = useMemo(() => `settings-field-${copyKey}-${++fieldIdSeq}`, [copyKey]);
  const error = fieldError(errors, copyKey);
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-slate-700 mb-1">
        {copy?.label ?? copyKey}
      </label>
      {children(id)}
      {copy?.help && <p className="mt-1 text-[11px] text-slate-500">{copy.help}</p>}
      {error && <p className="mt-1 text-[11px] text-danger-600">{error}</p>}
    </div>
  );
};

const Select: React.FC<{
  id?: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}> = ({ id, value, onChange, options }) => (
  <select
    id={id}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="block w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm bg-white
               focus:outline-none focus:ring-1 focus:ring-primary-400"
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

const NumberInput: React.FC<{
  id?: string;
  value: number;
  min?: number;
  max?: number;
  onChange: (n: number) => void;
}> = ({ id, value, min, max, onChange }) => (
  <input
    id={id}
    type="number"
    value={value}
    min={min}
    max={max}
    onChange={(e) => onChange(Number(e.target.value))}
    className="block w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm
               focus:outline-none focus:ring-1 focus:ring-primary-400"
  />
);

const Toggle: React.FC<{ id?: string; value: boolean; onChange: (v: boolean) => void }> = ({
  id,
  value,
  onChange,
}) => (
  <span className="inline-flex items-center gap-2">
    <input
      id={id}
      type="checkbox"
      checked={value}
      onChange={(e) => onChange(e.target.checked)}
      className="rounded"
    />
    <span className="text-sm text-slate-600">{value ? 'On' : 'Off'}</span>
  </span>
);

// ---------------------------------------------------------------------------
// Conflict banner — renders the server's current view, my draft, and the
// original side-by-side. The operator can accept the server version
// (discards their changes) or dismiss and merge manually.
// ---------------------------------------------------------------------------

const ConflictBanner: React.FC<{
  current: ProjectSettingsView;
  mine: ProjectSettings;
  original: ProjectSettings;
  onAcceptServer: () => void;
  onDismiss: () => void;
}> = ({ current, mine, original, onAcceptServer, onDismiss }) => (
  <div
    role="alert"
    data-testid="conflict-banner"
    className="border border-warning-200 bg-warning-50 rounded p-3 text-xs space-y-2"
  >
    <div className="flex items-center justify-between">
      <strong className="text-warning-900">Version conflict</strong>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onAcceptServer}
          className="px-2 py-0.5 border border-warning-200 bg-white rounded hover:bg-warning-100"
        >
          Accept server version (v{current.version})
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="px-2 py-0.5 border border-warning-200 bg-white rounded hover:bg-warning-100"
        >
          Dismiss
        </button>
      </div>
    </div>
    <p className="text-warning-900">
      Another editor saved version {current.version} while you were editing.
      Reconcile the differences and save again.
    </p>
    <div className="grid grid-cols-3 gap-2 mt-2">
      <DiffColumn title={`Original (v${current.version - 1})`} value={original} />
      <DiffColumn title="My draft" value={mine} highlight />
      <DiffColumn title={`Server (v${current.version})`} value={current.settings} />
    </div>
  </div>
);

const DiffColumn: React.FC<{ title: string; value: ProjectSettings; highlight?: boolean }> = ({
  title,
  value,
  highlight,
}) => (
  <div>
    <header className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">{title}</header>
    <pre
      className={
        'whitespace-pre-wrap text-[10px] border rounded p-1 max-h-48 overflow-auto ' +
        (highlight ? 'bg-warning-100' : 'bg-white')
      }
    >
      {JSON.stringify(value, null, 2)}
    </pre>
  </div>
);

export default SettingsTab;
